import { create } from "zustand";

/**
 * App-global notification store.
 *
 * The home for outcomes that must outlive the surface that produced them — a
 * modal closes, a card moves, a turn ends, but the error stays put until the
 * operator dismisses it. This is the "nothing silent" principle made literal:
 * a confirm-done that fails its merge used to vanish when you closed the Done
 * modal; now it also lands here.
 *
 * Errors and warnings persist until dismissed; success/info auto-expire (the
 * toast component owns the timer). In-memory by design — these survive a modal
 * close and a view switch, not a hard page reload (the durable copy of a real
 * failure lives on the task itself, e.g. the Needs-Input blocker).
 */
export type NotificationSeverity = "error" | "warning" | "success" | "info";

export interface Notification {
  id: string;
  severity: NotificationSeverity;
  /** One-line summary — the thing the operator reads first. */
  title: string;
  /** Optional diagnostic body (git output, stderr tail) — rendered monospace. */
  detail?: string;
  createdAt: number;
}

interface NotificationState {
  notifications: Notification[];
  notify: (n: Omit<Notification, "id" | "createdAt">) => string;
  dismiss: (id: string) => void;
  clearAll: () => void;
}

const MAX = 50;
let seq = 0;

export const useNotifications = create<NotificationState>((set) => ({
  notifications: [],
  notify: (n) => {
    seq += 1;
    const id = `ntf-${seq}`;
    const entry: Notification = { ...n, id, createdAt: Date.now() };
    // Newest first; cap the tail so a noisy session can't grow unbounded.
    set((s) => ({ notifications: [entry, ...s.notifications].slice(0, MAX) }));
    return id;
  },
  dismiss: (id) =>
    set((s) => ({ notifications: s.notifications.filter((x) => x.id !== id) })),
  clearAll: () => set({ notifications: [] }),
}));

/** Imperative helper for non-component call sites. */
export const notify = (n: Omit<Notification, "id" | "createdAt">) =>
  useNotifications.getState().notify(n);
